import { useEffect, useRef, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'src/trpc';
import { useSearchParams } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '../table/DataTable';
import { Loader } from '../Loader';
import { useDebounce } from '../Debounce';
import { LeadsColumns } from './LeadsColumns';
import { MarketingDashboard } from '../marketing/MarketingDashboard';
import { MainLayout } from 'src/pages/MainLayout';
import { Button } from '@/components/ui/button';
import { useToast } from '../useToast';
import { LeadExport } from 'src/trpc';

const exportLeads = (leads: LeadExport[]) => {
  const csvContent = [
    ['Phone', 'Date Created'],
    ...leads.map((lead) => [lead.phone, lead.createdAt]),
  ]
    .map((row) => row.join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'leads.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const LeadsTab = () => {
  const { successToast, errorToast } = useToast();
  const [params, setParams] = useSearchParams();

  const offset = parseInt(params.get('offset') || '0', 10) || 0;
  const limit = 25;
  const currentPage = offset === 0 ? 1 : offset / limit + 1;
  const { data, isPending, isFetching } = trpc.admin.leads.adminListLeads.useQuery(
    {
      limit,
      offset: offset,
      sortDirection: (params.get('sortDirection') as 'asc' | 'desc') || 'desc',
      sortKey: params.get('sortKey') || 'createdAt',
      search: params.get('search') || '',
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  // Seed from the URL: the tab switch unmounts this component, so defaulting here would let the
  // mount effects below write defaults back over the sort and search the admin had chosen.
  const [sorting, setSorting] = useState<SortingState>(() => [
    {
      id: params.get('sortKey') || 'createdAt',
      desc: (params.get('sortDirection') || 'desc') === 'desc',
    },
  ]);
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 500);

  // One effect for both, because two writing separately each build from the same captured params
  // and the second silently drops the first's keys. Skipped on mount: the state above was just
  // seeded from the URL, so there is nothing to write back and no page to reset.
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const newParams = new URLSearchParams(params);
    if (sorting.length) {
      newParams.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      newParams.set('sortKey', sorting[0].id);
    }
    newParams.set('search', debouncedSearch || '');
    newParams.set('offset', '0');
    setParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting, debouncedSearch]);

  const { mutate, isPending: isExporting } = trpc.admin.leads.adminExportLeads.useMutation({
    onSuccess: (data: { leads: LeadExport[] }) => {
      exportLeads(data.leads);
      successToast({
        title: 'Export Successful',
        description: 'The leads data has been exported successfully.',
      });
    },
    onError: () => {
      errorToast({
        title: 'Export Failed',
        description: 'There was an error exporting the leads data. Please try again.',
      });
    },
  });

  const handleExport = () => {
    mutate({ search: params.get('search') || '' });
  };

  if (isPending && isFetching && !data) {
    return <Loader title="Getting Leads" />;
  }

  return (
    <>
      <div className="flex flex-row justify-between">
        <div />
        <Button onClick={handleExport} disabled={isExporting}>
          {isExporting ? 'Exporting...' : 'Export Leads'}
        </Button>
      </div>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search leads by phone number"
      />
      <div className="flex flex-col h-full">
        <DataTable
          total={data?.total || 0}
          resourceName="leads"
          offset={offset}
          limit={limit}
          columns={LeadsColumns}
          data={data?.leads || []}
          sorting={sorting}
          onSortingChange={setSorting}
          hasNextPage={(data?.total || 0) > offset + limit}
          hasPreviousPage={offset > 0}
          onPageChange={(change) => {
            const newParams = new URLSearchParams(params);
            newParams.set('offset', (offset + change * limit).toString());
            setParams(newParams, { replace: true });
          }}
          currentPage={currentPage}
          pageCount={Math.ceil((data?.total || 0) / limit)}
        />
      </div>
    </>
  );
};

export const LeadsDashboard = () => {
  const [params, setParams] = useSearchParams();
  const activeTab = params.get('tab') || 'leads';

  const handleTabChange = (value: string) => {
    // Merge rather than replace so the list's search, sort and paging survive a tab switch.
    const newParams = new URLSearchParams(params);
    newParams.set('tab', value);
    setParams(newParams, { replace: true });
  };

  return (
    <MainLayout>
      <h1 className="text-3xl font-bold">Leads/Marketing</h1>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="marketing">Marketing</TabsTrigger>
        </TabsList>
        <TabsContent value="leads" className="mt-6 flex flex-col gap-4">
          <LeadsTab />
        </TabsContent>
        <TabsContent value="marketing" className="mt-6">
          <MarketingDashboard />
        </TabsContent>
      </Tabs>
    </MainLayout>
  );
};
